import {useMemo} from "react";
import MetricDashCard from "../../../../../../../../components/dash-card/metric-dash-card.jsx";
import SentryEchart from "../../../../../../../../components/echarts/sentry-echart.jsx";
import useGraphViewportWidth from "../../../../../../../../hooks/use-graph-viewport-width.js";
import {fluidPx} from "../../../../../../../../utils/responsive-graph.js";
import ProjectUtils from "../../../../../../../../utils/project-utils.js";

export default function AggressorsHistory({ data }) {
    const vw = useGraphViewportWidth();
    const educationLevels = data?.aggressors?.educationLevels;
    const gauges = Object.fromEntries(
        (data?.aggressors?.gauges ?? []).map(item => [item.label, item.qty])
    );

    const educationLevelsOption = useMemo(() => {
        const items = Array.isArray(educationLevels)
            ? educationLevels.map(item => ({
                label: item.label ?? "",
                value: Number(item.qty) || 0,
            }))
            : [];
        const labelFs = fluidPx(12, 21, vw);
        const valueFs = fluidPx(14, 20, vw);
        const max = Math.max(...items.map(item => item.value), 1);
        const labels = items.map(item => ProjectUtils.sentenceCase(item.label));
        const values = items.map(item => item.value);

        return {
            grid: {
                top: labelFs,
                left: "5%",
                right: "7%",
                bottom: 0,
                outerBounds: {
                    top: labelFs,
                    left: "5%",
                    right: "7%",
                    bottom: 0,
                },
                outerBoundsContain: "axisLabel",
            },
            tooltip: {
                show: true,
                appendToBody: true,
                confine: false,
                extraCssText: "max-width: 320px; white-space: normal;",
                formatter: params => `${params.name}: ${params.value}`,
            },
            xAxis: {
                type: "value",
                max,
                show: false,
            },
            yAxis: {
                type: "category",
                data: labels,
                inverse: true,
                axisLabel: {show: false},
                axisLine: {show: false},
                axisTick: {show: false},
            },
            series: [
                {
                    type: "bar",
                    barWidth: 13,
                    itemStyle: {
                        color: {
                            type: "linear",
                            x: 0,
                            y: 0,
                            x2: 1,
                            y2: 0,
                            colorStops: [
                                {offset: 0, color: "#003878"},
                                {offset: 0.28, color: "#0080ED"},
                                {offset: 0.52, color: "#1CEAE6"},
                                {offset: 0.72, color: "#3E98D9"},
                                {offset: 1, color: "#003878"},
                            ],
                        },
                        borderRadius: 20,
                    },
                    label: {
                        show: true,
                        position: [0, -2],
                        align: "left",
                        verticalAlign: "bottom",
                        color: "#AAAAAA",
                        fontSize: labelFs,
                        fontWeight: 400,
                        formatter: params => labels[params.dataIndex],
                    },
                    showBackground: true,
                    backgroundStyle: {
                        color: "#3C3C4A",
                        borderRadius: 12,
                    },
                    data: values,
                },
                {
                    type: "bar",
                    barWidth: 13,
                    barGap: "-100%",
                    silent: true,
                    tooltip: {show: false},
                    itemStyle: {color: "transparent"},
                    label: {
                        show: true,
                        position: "right",
                        distance: 5,
                        color: "#FFFFFF",
                        fontSize: valueFs,
                        fontWeight: 600,
                        formatter: params => values[params.dataIndex],
                    },
                    data: values.map(() => max),
                },
            ],
        };
    }, [educationLevels, vw]);

    return (
        <article className="col-span-4 box-border h-full w-full min-h-0 min-w-0 grid grid-cols-[minmax(0,1fr)] grid-rows-[88px_minmax(0,1fr)_88px] pl-4 pr-2">
            <section className="min-h-0 min-w-0 flex gap-4 px-3 mt-3">
                <MetricDashCard
                    diagonal
                    diagonalCounter
                    title="Mandados de prisão"
                    value={gauges["Mandado de prisão"] ?? 0}
                    width="100%"
                    height={78}
                    dashClassName="min-w-0"
                    pClassName="leading-[1.15]!"
                />
                <MetricDashCard
                    diagonal
                    title="Histórico de arma de fogo"
                    value={gauges["Histórico com arma de fogo"] ?? 0}
                    width="100%"
                    height={78}
                    dashClassName="min-w-0"
                    pClassName="leading-[1.15]!"
                />
            </section>
            <section className="min-h-0 min-w-0 overflow-hidden flex">
                <SentryEchart
                    className="h-full min-h-0 w-full"
                    option={educationLevelsOption}
                    notMerge
                    title="Escolaridade"
                    titleClass="mb-0! translate-y-2"
                />
            </section>
            <section className="min-h-0 min-w-0 flex justify-center gap-2 mt-3">
                <MetricDashCard
                    counterCard
                    title="Mora junto"
                    value={gauges["Mora junto"] ?? 0}
                    width={154}
                    height={76}
                />
                <MetricDashCard
                    counterCard
                    title="Grupo reflexivo"
                    value={gauges["Grupo Reflexivo"] ?? 0}
                    width={154}
                    height={76}
                />
            </section>
        </article>
    )
}
